import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MessageCircle, Heart, Send, Loader2, Trash2, LogIn, Reply } from 'lucide-react';
import { API, useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';
import { toast } from '../hooks/use-toast';

const Comments = ({ videoId }) => {
  const { user, login } = useAuth();
  const { lang } = useLang();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [replyTo, setReplyTo] = useState(null);
  const [posting, setPosting] = useState(false);

  const load = async () => {
    try { const r = await axios.get(`${API}/videos/${videoId}/comments`); setComments(r.data.comments || []); }
    catch (_) {} finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [videoId]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!user) { login(); return; }
    const txt = text.trim();
    if (!txt) return;
    setPosting(true);
    try {
      await axios.post(`${API}/videos/${videoId}/comments`, { text: txt, parent_id: replyTo }, { withCredentials: true });
      setText(''); setReplyTo(null);
      load();
    } catch (_) { toast({ title: 'Fel' }); }
    finally { setPosting(false); }
  };

  const toggleLike = async (id) => {
    if (!user) { login(); return; }
    try {
      const r = await axios.post(`${API}/comments/${id}/like`, {}, { withCredentials: true });
      setComments(prev => prev.map(c => c.id === id ? { ...c, likes_count: r.data.likes_count, likes: r.data.liked ? [...(c.likes || []), user.user_id] : (c.likes || []).filter(x => x !== user.user_id) } : c));
    } catch (_) {}
  };

  const remove = async (id) => {
    if (!window.confirm(lang === 'sv' ? 'Ta bort?' : 'حذف؟')) return;
    try { await axios.delete(`${API}/comments/${id}`, { withCredentials: true }); load(); } catch (_) {}
  };

  const topLevel = comments.filter(c => !c.parent_id);
  const repliesOf = (id) => comments.filter(c => c.parent_id === id);

  const CommentRow = ({ c, isReply = false }) => (
    <div className={`flex items-start gap-3 ${isReply ? 'mt-3' : ''}`}>
      <a href={`/u/${c.user_id}`} className="flex-shrink-0">
        {c.user_picture ? <img src={c.user_picture} alt={c.user_name} className="w-9 h-9 rounded-full object-cover" referrerPolicy="no-referrer" /> : <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center text-white text-sm font-bold">{(c.user_name?.[0] || 'U').toUpperCase()}</div>}
      </a>
      <div className="flex-1 min-w-0">
        <div className="bg-white/5 rounded-2xl px-3.5 py-2">
          <div className="flex items-baseline gap-2 mb-0.5">
            <a href={`/u/${c.user_id}`} className="text-[13px] font-bold text-white truncate hover:underline">{c.user_name || 'User'}</a>
            <span className="text-[10.5px] text-neutral-500">{new Date(c.created_at).toLocaleDateString(lang === 'sv' ? 'sv-SE' : 'ar-SA')}</span>
          </div>
          <div className="text-[13.5px] text-neutral-200 leading-snug break-words whitespace-pre-wrap">{c.text}</div>
        </div>
        <div className="mt-1.5 flex items-center gap-3 text-[11px]">
          <button onClick={() => toggleLike(c.id)} className={`inline-flex items-center gap-1 font-semibold transition-colors ${(c.likes || []).includes(user?.user_id) ? 'text-rose-400' : 'text-neutral-400 hover:text-white'}`}>
            <Heart className={`w-3 h-3 ${(c.likes || []).includes(user?.user_id) ? 'fill-rose-400' : ''}`} /> {c.likes_count || 0}
          </button>
          {!isReply && (
            <button onClick={() => setReplyTo(c.id)} className="inline-flex items-center gap-1 text-neutral-400 hover:text-white font-semibold">
              <Reply className="w-3 h-3" /> {lang === 'sv' ? 'Svara' : 'رد'}
            </button>
          )}
          {user?.user_id === c.user_id && (
            <button onClick={() => remove(c.id)} className="inline-flex items-center gap-1 text-neutral-500 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="mt-6 p-4 md:p-5 rounded-3xl bg-white/5 border border-white/10">
      <div className="flex items-center gap-2 mb-4">
        <MessageCircle className="w-4 h-4 text-cyan-400" />
        <span className="text-[11.5px] uppercase tracking-wider font-bold text-neutral-300">{lang === 'sv' ? 'Kommentarer' : 'التعليقات'}</span>
        <span className="text-[11.5px] text-neutral-500 font-bold">{comments.length}</span>
      </div>

      <form onSubmit={submit} className="mb-5">
        {replyTo && (
          <div className="mb-2 flex items-center gap-2 text-[11.5px] text-cyan-300">
            <Reply className="w-3 h-3" /> {lang === 'sv' ? 'Svarar' : 'رد على تعليق'}
            <button type="button" onClick={() => setReplyTo(null)} className="text-neutral-500 hover:text-white">×</button>
          </div>
        )}
        <div className="flex gap-2">
          {user ? (
            user.picture ? <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" referrerPolicy="no-referrer" /> : <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex-shrink-0" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0"><LogIn className="w-4 h-4 text-neutral-400" /></div>
          )}
          <div className="flex-1 flex gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder={user ? (lang === 'sv' ? 'Skriv en kommentar...' : 'اكتب تعليقاً...') : (lang === 'sv' ? 'Logga in för att kommentera' : 'سجّل الدخول للتعليق')} onClick={() => !user && login()} readOnly={!user} className="flex-1 h-11 px-4 rounded-full bg-white/5 border border-white/10 text-white text-[13.5px] placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent" />
            <button type="submit" disabled={posting || !text.trim()} className="h-11 w-11 rounded-full bg-cyan-500 hover:bg-cyan-600 disabled:opacity-40 text-white flex items-center justify-center flex-shrink-0">
              {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </form>

      {loading ? (
        <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-cyan-400" /></div>
      ) : topLevel.length === 0 ? (
        <div className="text-center py-8 text-neutral-500 text-[13px]">{lang === 'sv' ? 'Var först att kommentera!' : 'كن أول من يعلق!'}</div>
      ) : (
        <div className="space-y-5">
          {topLevel.map(c => (
            <div key={c.id}>
              <CommentRow c={c} />
              {repliesOf(c.id).length > 0 && (
                <div className="ml-12 rtl:ml-0 rtl:mr-12 mt-2 space-y-2">
                  {repliesOf(c.id).map(r => <CommentRow key={r.id} c={r} isReply />)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Comments;
